import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { articles, categories } from '@/mocks/data';

const articleImages: Record<string, string> = {
  'koujiao-jichu-ru-men-zhi-nan': 'https://readdy.ai/api/search-image?query=Warm%20soft%20minimalist%20abstract%20background%20with%20gentle%20rose%20and%20cream%20tones%2C%20flowing%20organic%20shapes%2C%20elegant%20texture%2C%20mood%20of%20intimacy%20and%20trust%2C%20editorial%20quality%20with%20subtle%20gradients%2C%20calm%20serene%20atmosphere%2C%20no%20human%20figures%2C%20artistic%20abstract%20composition&width=1200&height=500&seq=article-basic-guide&orientation=landscape',
  'koujiao-jiqiao-gaoji-zhi-yin': 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20composition%20with%20warm%20terracotta%20and%20cream%20gradients%2C%20flowing%20curves%20suggesting%20rhythm%20and%20harmony%2C%20soft%20lighting%20textures%2C%20artistic%20minimalist%20design%2C%20sophisticated%20editorial%20background%2C%20no%20figures%2C%20serene%20and%20intimate%20mood&width=1200&height=500&seq=article-advanced-skills&orientation=landscape',
  'koujiao-yu-annquan-xing-chuan-bo-ji-bing-fang-hu': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20abstract%20background%20with%20soft%20cream%20and%20warm%20neutral%20tones%2C%20geometric%20shield-like%20shapes%20subtly%20integrated%2C%20professional%20medical-educational%20aesthetic%2C%20calm%20trustworthy%20atmosphere%2C%20no%20human%20figures%2C%20editorial%20quality%20with%20soft%20lighting&width=1200&height=500&seq=article-safety-protection&orientation=landscape',
  'koujiao-ganran-zheng-zhuang-zao-qi-shi-bie': 'https://readdy.ai/api/search-image?query=Soft%20abstract%20medical-educational%20background%20with%20warm%20beige%20and%20cream%20tones%2C%20subtle%20geometric%20patterns%2C%20professional%20clinical%20aesthetic%20with%20human%20touch%2C%20calming%20trustworthy%20atmosphere%2C%20editorial%20quality%2C%20no%20human%20figures&width=1200&height=500&seq=article-symptoms&orientation=landscape',
  'koujiao-qian-hou-wei-sheng-zhi-nan': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20spa-like%20abstract%20background%20with%20soft%20cream%20and%20pale%20rose%20tones%2C%20water-like%20flowing%20textures%2C%20fresh%20pure%20atmosphere%2C%20editorial%20quality%2C%20calm%20and%20serene%20mood%2C%20no%20human%20figures%2C%20elegant%20composition&width=1200&height=500&seq=article-hygiene&orientation=landscape',
  'nan-xing-sheng-zhi-qi-wei-sheng-hu-li': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20background%20with%20warm%20neutral%20beige%20and%20cream%20gradients%2C%20clean%20modern%20aesthetic%2C%20bathroom-spa%20inspired%20soft%20textures%2C%20editorial%20quality%2C%20professional%20yet%20warm%20atmosphere%2C%20no%20human%20figures&width=1200&height=500&seq=article-male-hygiene&orientation=landscape',
  'yu-ban-lv-gou-tong-kou-jiao-xi-hao': 'https://readdy.ai/api/search-image?query=Warm%20intimate%20abstract%20background%20with%20soft%20rose%20and%20cream%20gradients%2C%20two%20flowing%20organic%20shapes%20intertwining%20harmoniously%2C%20gentle%20editorial%20aesthetic%2C%20mood%20of%20trust%20and%20closeness%2C%20no%20human%20figures%2C%20calm%20serene%20atmosphere&width=1200&height=500&seq=article-communication&orientation=landscape',
  'koujiao-zhong-xin-li-zhang-ai-ke-fu': 'https://readdy.ai/api/search-image?query=Soft%20abstract%20background%20with%20gentle%20warm%20beige%20and%20cream%20gradients%2C%20calming%20atmospheric%20shapes%2C%20mindfulness-inspired%20composition%2C%20peaceful%20meditative%20aesthetic%2C%20editorial%20quality%2C%20no%20human%20figures%2C%20serene%20tranquil%20mood&width=1200&height=500&seq=article-psychology&orientation=landscape',
  'koujiao-yu-zheng-ti-xing-jian-kang': 'https://readdy.ai/api/search-image?query=Scientific%20educational%20abstract%20background%20with%20warm%20cream%20and%20soft%20earth%20tones%2C%20subtle%20molecular-wave%20patterns%2C%20professional%20medical%20aesthetic%20with%20human%20warmth%2C%20editorial%20quality%2C%20no%20human%20figures%2C%20calm%20trustworthy%20atmosphere&width=1200&height=500&seq=article-health-science&orientation=landscape',
};

function getCategoryName(slug: string): string {
  const map: Record<string, string> = {
    jiqiao: '技巧',
    anquan: '安全',
    weisheng: '卫生',
    goutong: '沟通',
    xingjiankang: '健康',
  };
  return map[slug] || slug;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <Layout>
        <div className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const currentCat = categories.find((c) => c.slug === article.category);
  const h2Items = article.content.filter((c) => c.type === 'h2');

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    url: `https://climaxpro.github.io/article/${article.slug}`,
    author: {
      '@type': 'Organization',
      name: '亲密指南',
    },
    publisher: {
      '@type': 'Organization',
      name: '亲密指南',
    },
    datePublished: '2026-01-15',
    dateModified: '2026-08-01',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://climaxpro.github.io/article/${article.slug}`,
    },
    articleSection: getCategoryName(article.category),
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://climaxpro.github.io/' },
      { '@type': 'ListItem', position: 2, name: getCategoryName(article.category), item: `https://climaxpro.github.io/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://climaxpro.github.io/article/${article.slug}` },
    ],
  };

  return (
    <Layout>
      <SEOHead
        title={`${article.title} - 亲密指南`}
        description={article.summary}
        keywords={`口交,${getCategoryName(article.category)},性健康,成人亲密关系指南,性教育知识`}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        articlePublishedTime="2026-01-15"
        articleModifiedTime="2026-08-01"
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <article className="pt-20 pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: getCategoryName(article.category), to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                {getCategoryName(article.category)}
              </span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">
              {article.summary}
            </p>
          </div>

          <div className="relative h-[300px] md:h-[400px] rounded-2xl overflow-hidden mb-12">
            <img
              src={articleImages[article.slug] || ''}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-12">
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-24 bg-background-100 rounded-xl p-5 border border-background-200/70">
                <h4 className="text-sm font-semibold text-foreground-900 mb-4">文章目录</h4>
                <nav>
                  <ol className="space-y-2">
                    {h2Items.map((item, i) => (
                      <li key={i}>
                        <a
                          href={`#section-${i}`}
                          className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer leading-relaxed block"
                        >
                          {item.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="prose-content">
                {article.content.map((block, i) => {
                  if (block.type === 'h2') {
                    const h2Index = h2Items.indexOf(block);
                    return (
                      <h2 key={i} id={`section-${h2Index}`} className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === 'h3') {
                    return (
                      <h3 key={i} className="text-lg font-heading font-semibold text-foreground-900 mt-8 mb-3">
                        {block.text}
                      </h3>
                    );
                  }
                  return (
                    <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {block.text}
                    </p>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-8">
              相关文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 rounded-xl p-5 cursor-pointer border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2">
                    {ra.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                    {ra.summary}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs text-foreground-400">{ra.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-16 text-center">
        <p className="text-sm text-foreground-500">
          本文内容仅供成年读者教育参考，不构成医疗建议。如有健康疑虑，请咨询专业医生。
        </p>
      </div>
    </Layout>
  );
}